import { createApp } from 'vue'
import App from './App.vue'
import store from './store'
import vuetify from './plugins/vuetify'
import Dialog2 from '@/components/Dialog2'
import VueQrcode from '@chenfengyuan/vue-qrcode'
import { auth, db } from './firebase'
import '@/registerServiceWorker'
import VDialogConfirm from '@/components/V-dialog/V-dialog-confirm.vue'
import VDialogCommon from '@/components/V-dialog/V-dialog-common'
import trackDirective from '@/js/directives/track'
import '@/styles/design-system.css'
import Vue3Tour from 'vue3-tour'
import 'vue3-tour/dist/vue3-tour.css'

// MapLibre GL HMRエラー抑制（最優先で実行）
;(() => {
  const originalError = console.error
  const originalWarn = console.warn

  const shouldSuppress = (args) => {
    const message = args.join(' ')
    // MapLibre GL HMRエラーをフィルタリング
    return (
      (message.includes("'get' on proxy") && message.includes('rgb')) ||
      (message.includes('read-only and non-configurable') && message.includes('rgb')) ||
      (message.includes('Cannot read properties of undefined') && message.includes("reading 'get'")) ||
      message.includes('continuePlacement') ||
      message.includes('_updatePlacement')
    )
  }

  console.error = function (...args) {
    if (shouldSuppress(args)) {
      console.debug('[MapLibre HMR] Suppressed:', args[0])
      return
    }
    originalError.apply(console, args)
  }

  console.warn = function (...args) {
    if (shouldSuppress(args)) {
      return
    }
    originalWarn.apply(console, args)
  }
})()

// Unhandled rejectionエラーも抑制
window.addEventListener('unhandledrejection', (event) => {
  const message = event.reason?.message || String(event.reason)
  if (
    (message.includes("'get' on proxy") && message.includes('rgb')) ||
    (message.includes('read-only and non-configurable') && message.includes('rgb')) ||
    (message.includes('Cannot read properties of undefined') && message.includes("reading 'get'")) ||
    message.includes('continuePlacement') ||
    message.includes('_updatePlacement')
  ) {
    console.debug('[MapLibre HMR] Unhandled rejection suppressed:', message)
    event.preventDefault()
  }
})

// ディレクティブ
const stickBottomV3 = {
  mounted(el, binding) {
    const getOpts = (b) => ({
      threshold: b && b.value && typeof b.value.threshold === 'number' ? b.value.threshold : 40,
      useSmooth: !!(b && b.modifiers && b.modifiers.smooth),
    })
    let opts = getOpts(binding)
    const isNearBottom = () => el.scrollHeight - el.clientHeight - el.scrollTop <= opts.threshold
    const scrollToBottom = () => {
      el.scrollTo({ top: el.scrollHeight, behavior: opts.useSmooth ? 'smooth' : 'auto' })
    }
    const state = { stick: true }
    const onScroll = () => {
      state.stick = isNearBottom()
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    const mo = new MutationObserver(() => {
      if (state.stick) scrollToBottom()
    })
    mo.observe(el, { childList: true, subtree: true })
    let ro
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(() => {
        if (state.stick) scrollToBottom()
      })
      ro.observe(el)
    }
    requestAnimationFrame(scrollToBottom)
    el._stickBottomCleanup = () => {
      el.removeEventListener('scroll', onScroll)
      mo.disconnect()
      ro?.disconnect()
    }
  },
  updated(el, binding) {
    const threshold = binding.value && typeof binding.value.threshold === 'number' ? binding.value.threshold : 40
    const useSmooth = !!(binding.modifiers && binding.modifiers.smooth)
    const atBottom = el.scrollHeight - el.clientHeight - el.scrollTop <= threshold
    if (atBottom) el.scrollTo({ top: el.scrollHeight, behavior: useSmooth ? 'smooth' : 'auto' })
  },
  unmounted(el) {
    el._stickBottomCleanup?.()
  },
}

const app = createApp(App)

// Suppress Vuetify "elevation" warnings (known bug in Vuetify 3)
app.config.warnHandler = (msg, instance, trace) => {
  // Ignore specific Vuetify elevation warnings
  if (msg.includes('Computed property "elevation" is already defined in Props')) {
    return
  }
  // Ignore FloatingWindow emits warnings
  if (msg.includes('Extraneous non-emits event listeners')) {
    return
  }
  // Log all other warnings
  console.warn(`[Vue warn]: ${msg}`, trace)
}

// Suppress MapLibre GL HMR errors in development
app.config.errorHandler = (err, instance, info) => {
  // MapLibre GL HMRエラーを抑制
  if (
    err &&
    err.message &&
    ((err.message.includes("'get' on proxy") && err.message.includes('rgb')) ||
      (err.message.includes('read-only and non-configurable') && err.message.includes('rgb')) ||
      (err.message.includes('Cannot read properties of undefined') && err.message.includes("reading 'get'")) ||
      err.message.includes('continuePlacement') ||
      err.message.includes('_updatePlacement'))
  ) {
    // 完全に無視（console.warnも出さない）
    return
  }
  // Log other errors normally
  console.error('[Vue Error]:', err, info)
}

// Window-level error handler for errors that escape Vue's error handler
window.addEventListener('error', (event) => {
  if (
    event.error &&
    event.error.message &&
    ((event.error.message.includes("'get' on proxy") && event.error.message.includes('rgb')) ||
      (event.error.message.includes('read-only and non-configurable') && event.error.message.includes('rgb')) ||
      (event.error.message.includes('Cannot read properties of undefined') &&
        event.error.message.includes("reading 'get'")) ||
      event.error.message.includes('continuePlacement') ||
      event.error.message.includes('_updatePlacement'))
  ) {
    // 完全に無視
    event.preventDefault()
    return false
  }
})

app.use(store)
app.use(vuetify)
app.use(Vue3Tour)
app.component('Dialog2', Dialog2)
app.component('VDialogCommon', VDialogCommon)
app.component(VueQrcode.name, VueQrcode)
app.component('VDialogConfirm', VDialogConfirm)
app.directive('stick-bottom', stickBottomV3) // ディレクティブをセット
app.directive('track', trackDirective) // トラッキングディレクティブ

app.mount('#app')

store.state.oh3App = app

// ✅ mount 後に実行
auth.onAuthStateChanged(async (user) => {
  if (!user) return

  try {
    const userDoc = await db.collection('users').doc(user.uid).get()
    const groupIds = userDoc.exists ? userDoc.data().groups || [] : []

    if (!Array.isArray(groupIds) || groupIds.length === 0) {
      return
    }

    const groupDocPromises = groupIds.map((groupId) =>
      db
        .collection('groups')
        .doc(groupId)
        .get()
        .catch((error) => {
          console.error('グループ情報の取得に失敗しました:', groupId, error)
          return null
        })
    )

    for (let index = 0; index < groupDocPromises.length; index += 1) {
      const groupDoc = await groupDocPromises[index]
      if (groupDoc && groupDoc.exists) {
        const groupName = groupDoc.data().name
        store.commit('setCurrentGroupId', groupIds[index])
        store.commit('setCurrentGroupName', groupName)
        break
      }
    }
  } catch (error) {
    console.error('ユーザードキュメントの取得に失敗しました:', error)
  }
})

store.state.isIphone = /iPhone/i.test(navigator.userAgent)
store.state.isAndroid = /Android/i.test(navigator.userAgent)

async function checkVersion() {
  try {
    // ブラウザモード無限ループ防止: URLパラメータチェック（確実）
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.has('_just_updated')) {
      // URLパラメータをクリーンアップ（履歴を汚さない）
      const cleanUrl = window.location.pathname
      window.history.replaceState({}, '', cleanUrl)
      return
    }

    // Chrome iOSの無限ループ防止: 更新直後はチェックをスキップ（localStorage版）
    const skipFlag = localStorage.getItem('oh3_skip_next_version_check')
    if (skipFlag) {
      const skipTime = Number(skipFlag)
      const elapsed = Date.now() - skipTime
      // 60秒以内ならスキップ
      if (elapsed < 60000) {
        return
      }
      // 60秒経過したらフラグをクリア
      localStorage.removeItem('oh3_skip_next_version_check')
    }

    const response = await fetch('/oh3/php/appconfig_select.php', {
      method: 'POST',
    })
    const result = await response.json()
    if (result.success) {
      const appVersion = Number(result.row.version)
      if (appVersion > store.state.clientVersion) {
        store.state.updatePermalinkFire = !store.state.updatePermalinkFire
        // ★ バージョン更新ダイアログを復活
        store.state.commonDialog.versionMessage = true
      }
    }
  } catch (e) {
    console.error('バージョンチェックに失敗:', e)
  }
}
let versionTimerId
const runVersionCheck = () => {
  if (document.visibilityState !== 'visible') {
    return
  }
  checkVersion()
}

const scheduleVersionCheck = () => {
  if (versionTimerId) {
    return
  }

  runVersionCheck()
  versionTimerId = setInterval(runVersionCheck, 10 * 60 * 1000)
}

const stopVersionCheck = () => {
  if (versionTimerId) {
    clearInterval(versionTimerId)
    versionTimerId = null
  }
}

if (document.visibilityState === 'visible') {
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(scheduleVersionCheck, { timeout: 2000 })
  } else {
    setTimeout(scheduleVersionCheck, 500)
  }
} else {
  const onVisible = () => {
    if (document.visibilityState === 'visible') {
      scheduleVersionCheck()
      document.removeEventListener('visibilitychange', onVisible)
    }
  }
  document.addEventListener('visibilitychange', onVisible, { once: true })
}

// タブ非表示時にバージョンチェックを停止、再表示時に再開（メモリリーク防止）
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') {
    stopVersionCheck()
  } else {
    scheduleVersionCheck()
  }
})

// // どこか起動時に一度だけ
// store.watch(
//     state => state.saveHistoryFire,
//     (newVal, oldVal) => {
//         console.group('★ saveHistoryFire changed ★');
//         console.trace();  // コールスタックを出力
//         console.groupEnd();
//     }
// );
